import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { getNavigationByView } from '@/config/navigationConfig';
import AvisoOutroCanalRota from '@/features/auth/components/AvisoOutroCanal';
import '@/features/auth/components/ProtectedView.css';

export interface ProtectedViewProps {
  children: React.ReactNode;
  permission?: string | string[];
  screen?: string;
  action?: 'VIEW' | 'CREATE' | 'EDIT' | 'DELETE' | 'ADMIN';
  requireAll?: boolean;
  fallback?: React.ReactNode;
  loadingComponent?: React.ReactNode;
  onAccessDenied?: (reason: string) => void;
  onAccessGranted?: () => void;
  /** Way back from the "available in the Joshua app" notice (JOSH-91). */
  onVoltarAoInicio?: () => void;
  enableAudit?: boolean;
  metadata?: Record<string, any>;
}

/**
 * ProtectedView Component
 *
 * Provides permission-based access control using useAuth hook.
 * - Screen and permission-based access control
 * - Loading states during permission validation
 * - Fallback UI for unauthorized access
 */
export default function ProtectedView({
  children,
  permission,
  screen,
  action = 'VIEW',
  requireAll = false,
  fallback = null,
  loadingComponent = null,
  onAccessDenied,
  onAccessGranted,
  onVoltarAoInicio,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  enableAudit = false, // Mantido para compatibilidade, mas não usado
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  metadata = {},
  ...props
}: ProtectedViewProps): React.ReactElement {
  const {
    isAuthenticated,
    loading: authLoading,
    authError,
    securityStatus,
    validateAuthState,
    hasPermission,
    temVisualizarEmOutroCanal
  } = useAuth();

  const [accessGranted, setAccessGranted] = useState<boolean>(false);
  const [sessionRetryCount, setSessionRetryCount] = useState<number>(0);

  // Determine required permission - lookup from navigationConfig if not provided
  const requiredPermission = React.useMemo(() => {
    if (permission) return permission;
    if (screen) {
      const navConfig = getNavigationByView(screen);
      return navConfig?.permission || null;
    }
    return null;
  }, [permission, screen]);

  // JOSH-91: recurso whose only views are on the other channel. Computed during
  // render (access state is set in an effect and starts false), so the notice
  // shows on the first render and never flashes "Acesso Negado". A wrong-channel
  // view wins unless another guarded recurso has a view that counts here.
  const recursoNoOutroCanal = React.useMemo((): string | null => {
    if (authLoading || !isAuthenticated) return null;
    const recursos = new Set<string>();
    const permissionList = !requiredPermission
      ? []
      : Array.isArray(requiredPermission)
        ? requiredPermission
        : [requiredPermission];
    permissionList.forEach(perm => recursos.add(perm.toUpperCase()));
    if (recursos.size === 0) return null;
    const lista = [...recursos];
    if (lista.some(recurso => hasPermission(recurso, 'view'))) return null;
    return lista.find(recurso => temVisualizarEmOutroCanal(recurso)) ?? null;
  }, [authLoading, isAuthenticated, requiredPermission, hasPermission, temVisualizarEmOutroCanal]);

  // Permission validation and access control
  const checkAccess = useCallback((): boolean => {
    try {
      // Basic authentication check
      if (!isAuthenticated) {
        return false;
      }

      // If no permission required, grant access
      if (!requiredPermission && !screen) {
        return true;
      }

      // Permission-based access check
      if (requiredPermission) {
        const permissionList = Array.isArray(requiredPermission)
          ? requiredPermission
          : [requiredPermission];

        // Check if user has any of the required permissions (unless requireAll is true)
        const hasAccess = requireAll
          ? permissionList.every(perm => hasPermission(perm))
          : permissionList.some(perm => hasPermission(perm));

        return hasAccess;
      }

      // Default: grant access if no specific requirements
      return true;

    } catch {
      // In case of error, fail closed (deny access)
      return false;
    }
  }, [
    isAuthenticated,
    requiredPermission,
    screen,
    requireAll,
    hasPermission
  ]);

  // Access control effect
  useEffect(() => {
    if (authLoading) return;
    // The channel notice is not a denial: no callbacks, so the Router does not
    // reset to Início.
    if (recursoNoOutroCanal) return;

    const hasAccess = checkAccess();
    setAccessGranted(hasAccess);

    // Callbacks
    if (hasAccess) {
      onAccessGranted?.();
    } else {
      const reason = !isAuthenticated
        ? 'User not authenticated'
        : `Missing permission: ${requiredPermission || 'Unknown'}`;
      onAccessDenied?.(reason);
    }

  }, [
    authLoading,
    recursoNoOutroCanal,
    checkAccess,
    requiredPermission,
    isAuthenticated,
    onAccessGranted,
    onAccessDenied
  ]);

  // Renderização de problemas de sessão/conectividade
  const renderSessionIssues = () => {
    // Estado inconsistente crítico
    if (securityStatus === 'inconsistent') {
      return (
        <div className="protected-view-session-issue">
          <div className="session-issue-content">
            <div className="session-issue-icon">⚠️</div>
            <h3 className="session-issue-title">Problema de Sessão</h3>
            <p className="session-issue-message">
              Sua sessão apresenta inconsistências. Para sua segurança, algumas funcionalidades podem estar limitadas.
            </p>
            {authError && (
              <div className="session-issue-details">
                <strong>Detalhes:</strong> {authError}
              </div>
            )}
            <div className="session-issue-actions">
              <button
                onClick={async () => {
                  setSessionRetryCount(prev => prev + 1);
                  await validateAuthState();
                }}
                className="retry-session-btn"
                disabled={sessionRetryCount >= 3}
              >
                {sessionRetryCount >= 3 ? 'Limite de tentativas atingido' : 'Tentar Novamente'}
              </button>
              <button
                onClick={() => window.location.reload()}
                className="refresh-page-btn"
              >
                Recarregar Página
              </button>
            </div>
          </div>
        </div>
      );
    }

    return null;
  };

  // Loading state
  if (authLoading) {
    if (loadingComponent) {
      return <div className="protected-view-loading-wrapper">{loadingComponent}</div>;
    }

    return (
      <div className="protected-view-loading">
        <div className="loading-spinner">
          <div className="spinner-icon">🔄</div>
          <p className="loading-text">Verificando autenticação...</p>
        </div>
      </div>
    );
  }

  // Inconsistent session: block the content completely.
  const sessionIssueComponent = renderSessionIssues();
  if (sessionIssueComponent) {
    return sessionIssueComponent;
  }

  // Not authenticated
  if (!isAuthenticated) {
    return renderAccessDenied(
      '🔒',
      'Acesso Negado',
      'Você precisa estar logado para acessar esta funcionalidade.',
      'Faça login para continuar'
    );
  }

  // JOSH-91: the view is on the other channel; hand the person over to the app.
  if (recursoNoOutroCanal) {
    return (
      <AvisoOutroCanalRota recurso={recursoNoOutroCanal} onVoltarAoInicio={onVoltarAoInicio} />
    );
  }

  // Access denied
  if (!accessGranted) {
    if (fallback) {
      return <div className="protected-view-fallback">{fallback}</div>;
    }

    const permissionText = Array.isArray(requiredPermission)
      ? requiredPermission.join(', ')
      : requiredPermission;

    return renderAccessDenied(
      '🚫',
      'Acesso Negado',
      'Você não tem permissão para acessar esta funcionalidade.',
      screen
        ? `Tela: ${screen} (${action})`
        : `Permissão necessária: ${permissionText}`
    );
  }

  // Access granted. A reconnecting session is shown by the shell-level
  // ReconnectingPill (JOSH-89), not here.
  return (
    <div className="protected-view-container" {...props}>
      <div className="protected-view-content">
        {children}
      </div>
    </div>
  );

  function renderAccessDenied(
    icon: string,
    title: string,
    message: string,
    details?: string
  ) {
    return (
      <div className="protected-view-access-denied">
        <div className="access-denied-content">
          <div className="access-denied-icon">{icon}</div>
          <h2 className="access-denied-title">{title}</h2>
          <p className="access-denied-message">
            {message}
            {details && (
              <span className="access-denied-details">
                <br />
                <strong>{details}</strong>
              </span>
            )}
          </p>

          <p className="access-denied-help">
            Entre em contato com um administrador do sistema para solicitar acesso.
          </p>

          {/* Debug information (development only) */}
          {import.meta.env.DEV && (
            <details className="debug-info">
              <summary>Debug Info</summary>
              <pre>
                {JSON.stringify({
                  screen,
                  permission: requiredPermission,
                  action,
                  requireAll,
                  isAuthenticated
                }, null, 2)}
              </pre>
            </details>
          )}
        </div>
      </div>
    );
  }
}
